import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

// Animation variant
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const PrivacyPolicyPage = () => {
  return (
    <div className="w-full pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-gray-100" data-testid="privacy-hero">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Link to="/" className="inline-flex items-center text-brand-blue hover:text-cyan-500 mb-6 transition-colors font-medium uppercase">
              <ArrowLeft size={20} className="mr-2" />
              BACK TO HOME
            </Link>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-gray-900 uppercase">
              PRIVACY POLICY
            </h1>
            <p className="text-gray-500 mt-4">Last updated: December 2025</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="prose prose-lg max-w-none">
            
            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">INTRODUCTION</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Zenith Marketing India ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with our services.
            </p>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">INFORMATION WE COLLECT</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We may collect information about you in various ways, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li><strong className="text-gray-900">Personal Data:</strong> Name, email address, phone number, company name, and other contact information you voluntarily provide when filling out forms or contacting us.</li>
              <li><strong className="text-gray-900">Usage Data:</strong> Information about how you use our website, including pages visited, time spent, and navigation patterns.</li>
              <li><strong className="text-gray-900">Device Information:</strong> Information about your device, browser type, IP address, and operating system.</li>
            </ul>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">HOW WE USE YOUR INFORMATION</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We use the information we collect for various purposes, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>To respond to your inquiries and provide customer support</li>
              <li>To send you marketing communications (with your consent)</li>
              <li>To improve our website and services</li>
              <li>To analyze usage patterns and optimize user experience</li>
              <li>To comply with legal obligations</li>
            </ul>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">INFORMATION SHARING</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share information with trusted third-party service providers who assist us in operating our website and conducting our business, provided they agree to keep this information confidential.
            </p>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">DATA SECURITY</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">COOKIES</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse cookies, but some features of our website may not function properly without them.
            </p>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">THIRD-PARTY LINKS</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">YOUR RIGHTS</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>Access and receive a copy of your personal data</li>
              <li>Request correction of inaccurate personal data</li>
              <li>Request deletion of your personal data</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent at any time</li>
            </ul>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">CHILDREN'S PRIVACY</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us.
            </p>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">CHANGES TO THIS POLICY</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>

            <h2 className="font-outfit text-2xl font-bold text-gray-900 mt-8 mb-4 uppercase">CONTACT US</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200 mb-6">
              <p className="text-gray-900 font-semibold mb-2">ZENITH MARKETING INDIA</p>
              <p className="text-gray-600">D-10, S2, Airport Enclave</p>
              <p className="text-gray-600">Jagatpura, Jaipur, Rajasthan, India</p>
              <p className="text-gray-600 mt-2">
                Email: <a href="mailto:info@zenithmarketingindia.com" className="text-brand-blue hover:text-cyan-500 transition-colors">info@zenithmarketingindia.com</a>
              </p>
              <p className="text-gray-600">
                Phone: <a href="tel:+919928279319" className="text-brand-blue hover:text-cyan-500 transition-colors">+91 9928279319</a>
              </p>
            </div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
